import SelectField from '@components/Inputs/SelectField';
import {
	Autocomplete,
	Box,
	Grid,
	IconButton,
	SelectChangeEvent,
	TextField
} from '@mui/material';
import { FormFieldType, IFormFieldRow } from 'types/common';
import { isObject } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { isOptionEqualToValue } from '@utils/forms';
import {
	authInfoConditionKeys,
	coreConditionKeys
} from '@components/Flows/Overlays/TaskConditionsDialog/AtomicCondition/constants';

interface IFieldRowProps {
	onChange: (index: number, row: IFormFieldRow) => void;
	onDelete: (index: number) => void;
	row: IFormFieldRow;
	index: number;
	deletable: boolean;
	name?: string;
}

const getContextKeys = () =>
	[...coreConditionKeys, ...authInfoConditionKeys]
		.filter((v) => v.tags)
		.map((v) => ({
			value: v.name,
			label: v.name
		}));

const getDefaultValue = (t: FormFieldType): any => {
	switch (t) {
		case FormFieldType.boolean:
			return false;
		case FormFieldType.number:
			return 0;
		case FormFieldType.string:
		default:
			return '';
	}
};

const FieldRow = ({
	name,
	index,
	row,
	onChange,
	onDelete,
	deletable
}: IFieldRowProps) => {
	const fieldTypes = useMemo(
		() =>
			Object.values(FormFieldType).map((v) => ({
				value: v,
				label: intl.tr(`lang.${v}`)
			})),
		[]
	);

	const valueOptions: { value: string; label: string }[] = useMemo(() => {
		switch (row.type) {
			case FormFieldType.boolean:
				return [
					{ value: 'true', label: intl.tr('lang.true') },
					{ value: 'false', label: intl.tr('lang.false') }
				];
			case FormFieldType.context:
				return getContextKeys();
			default:
				return [];
		}
	}, [row.type]);
	const handleDelete = useCallback(() => {
		onDelete(index);
	}, [onDelete, index]);
	const handleKeyChange = useCallback(
		(_: any, v: any) => {
			onChange(index, { ...row, key: v });
		},
		[onChange, index, row]
	);
	const handleTypeChange = useCallback(
		(e: SelectChangeEvent<unknown>) => {
			const newType = e.target.value as FormFieldType;
			onChange(index, {
				...row,
				type: newType,
				value: getDefaultValue(newType)
			});
		},
		[onChange, index, row]
	);
	const handleValueChange = useCallback(
		(_: any, value: any) => {
			let v: any = value;
			if (v === null || v === undefined) {
				v = getDefaultValue(row.type as FormFieldType);
			}
			if (isObject(value)) {
				v = (value as { value: string }).value;
			}
			switch (row.type) {
				case FormFieldType.boolean:
					v = Boolean(v);
					break;
				case FormFieldType.number:
					v = Number(v);
					break;
				case FormFieldType.string:
				default:
					try {
						v = JSON.parse(v);
						// eslint-disable-next-line no-empty
					} catch (e) {}
					break;
			}
			onChange(index, { ...row, value: v });
		},
		[onChange, index, row]
	);
	return (
		<Grid container spacing={1} mb={2} data-testid={`field-row-key-${index}`}>
			<Grid item xs={3}>
				<Autocomplete
					fullWidth
					id={`${name}[${index}].key`}
					freeSolo
					blurOnSelect
					autoSelect
					options={[]}
					value={row.key || ''}
					onChange={handleKeyChange}
					renderInput={(params) => (
						<TextField
							{...params}
							required
							label={intl.tr('input.object.key')}
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid item xs={3}>
				<SelectField
					value={row.type}
					name={`${name}[${index}].type`}
					label={intl.tr('input.object.type')}
					options={fieldTypes}
					onChange={handleTypeChange}
					inputProps={{
						'data-testid': `field-row-type-${index}`
					}}
				/>
			</Grid>
			<Grid item xs>
				<Autocomplete
					data-testid={`field-row-value-${index}`}
					fullWidth
					id={`${name}[${index}].value`}
					freeSolo
					blurOnSelect
					options={valueOptions}
					inputValue={`${
						isObject(row.value) ? JSON.stringify(row.value) : row.value
					}`}
					value={valueOptions ? row.value : undefined}
					onChange={handleValueChange}
					isOptionEqualToValue={isOptionEqualToValue}
					getOptionLabel={(opt: any) => `${opt?.label || opt}`}
					renderInput={(params) => (
						<TextField
							{...params}
							onChange={(e) => handleValueChange(e, e.target.value)}
							label={intl.tr('input.object.value')}
							fullWidth
						/>
					)}
				/>
			</Grid>
			{deletable && (
				<Grid item alignItems="center" xs="auto">
					<Box display="flex" flexDirection="row" justifyItems="center">
						<IconButton
							onClick={handleDelete}
							aria-label={intl.tr('button.delete')}
							data-testid="field-row-delete-button"
						>
							<DeleteIcon />
						</IconButton>
					</Box>
				</Grid>
			)}
		</Grid>
	);
};

FieldRow.defaultProps = {
	name: undefined
};

export type { IFieldRowProps };
export { getContextKeys };
export default FieldRow;
